<template>
  <div class="p-3">
    <div class="flex justify-end items-center">
      <div
        class="justify-end"
        :class="[$vuetify.breakpoint.lgAndUp ? 'flex' : 'flex-col']"
      >
        <v-col
          :cols="$vuetify.breakpoint.lgAndUp ? '3 pt-2 pb-3' : '12 py-2 pb-3'"
          :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
        >
          <v-autocomplete
            class="wfm-search-fields"
            v-model="filterEmployee"
            label="Select Assignee"
            placeholder="Search Assignee"
            :items="users"
            item-text="fullName"
            item-value="_id"
            solo
            flat
            clearable
            outlined
            hide-details
            @change="handleInput"
          ></v-autocomplete>
        </v-col>
        <v-col
          :cols="$vuetify.breakpoint.lgAndUp ? '3 pt-2 pb-3' : '12 py-2 pb-3'"
          :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
        >
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="Select Date Range"
                placeholder="Select Date Range"
                v-model="formattedDate"
                prepend-inner-icon="mdi-calendar"
                readonly
                hide-details
                outlined
                dense
                clearable
                v-on="on"
                @click:clear="onDateCleared"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              @input="onDateSelected"
              no-title
              @change="menu = false"
              range
              scrollable
            />
          </v-menu>
        </v-col>
      </div>

      <v-btn
        :color="$vuetify.theme.currentTheme.primary"
        class="ml-2 mb-2 white--text"
        v-tooltip="{
          text: 'Export Analytics',
        }"
        v-show="canEdit"
        @click="exportToXLSX"
      >
        Export Analytics
        <v-icon right dark>mdi-download</v-icon>
      </v-btn>
    </div>
    <div class="overflow-x-auto overflow-y-auto">
      <v-data-table
        :headers="headers"
        :items="analytics"
        :items-per-page="5"
        class="pl-7 pr-7 pt-4"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        fixed-header
      >
        <template v-slot:item="{ item }">
          <tr>
            <td
              v-for="(column, index) in headers"
              :key="index"
              class="px-4 py-6 font-semibold"
            >
              {{ item[column.value] }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import {
  fullName,
  hasPermission,
  convertNumberToAmount,
  mergeOptions,
  mergeOptionsDetails,
} from "@/utils/common";
import permissionData from "@/utils/permissions";
export default {
  props: {
    caseConfig: Object,
    employees: Array,
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      headers: [],
      analytics: [],
      dates: [],
      filterEmployee: "",
      startDate: null,
      menu: false,
      endDate: null,
      unAttended: "UN-ATTENDED/IN-COMPLETE",
    };
  },
  computed: {
    users() {
      return this.employees.map((emp) => ({
        ...emp,
        fullName: fullName(emp),
      }));
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.collectionEdit);
    },
    formattedDate() {
      if (this.dates.length === 2) {
        const startDate = this.visibleDate(this.dates[0]);
        const endDate = this.visibleDate(this.dates[1]);
        return `${startDate} to ${endDate}`;
      } else if (this.dates.length === 1) {
        return this.visibleDate(this.dates[0]);
      }
      return "";
    },
  },
  methods: {
    async fetchCaseAnalytics() {
      try {
        const params = {
          caseType: this.caseConfig?.name,
        };
        if (this.startDate && this.endDate) {
          params.start = this.startDate;
          params.end = this.endDate;
        }
        if (this.filterEmployee) {
          params.search = this.filterEmployee;
        }
        const response = await this.$axios.get(
          "/workforce/collection/cases/analytics",
          {
            params,
          }
        );

        const userCollections =
          response.data?.caseAnalytics.userCollectionStats.reduce(
            (collections, el) => {
              if (!collections[el.assignedTo]) {
                collections[el.assignedTo] = [];
              }
              collections[el.assignedTo] = collections[el.assignedTo].concat(
                el.fundsByOutcome
              );
              return collections;
            },
            {}
          );

        const data = response.data?.caseAnalytics.userDefinedAnalytics
          .map((el) => {
            const allStatuses = el.statuses.reduce(
              (acc, { status, totalCount }) => {
                if (status) {
                  acc[status] = totalCount;
                  acc[this.unAttended] += totalCount;
                }
                return acc;
              },
              { [this.unAttended]: 0 }
            );

            allStatuses[this.unAttended] =
              el.globalCount - allStatuses[this.unAttended];

            mergeOptions(this.caseConfig.userDefinedCaseStatus).forEach(
              (status) => {
                if (!allStatuses[status]) {
                  allStatuses[status] = 0;
                }
              }
            );

            const amountCollected = mergeOptionsDetails(
              this.caseConfig.userDefinedCaseStatus
            )
              .filter((option) => option.amountVisibility)
              .reduce((acc, option) => {
                const matchedOutcome = userCollections[el.assignedTo]?.find(
                  (fund) => fund.outcome === option.code
                );
                acc[`${option.code}_amount`] = matchedOutcome?.amount || 0;
                return acc;
              }, {});

            // Construct userStatus
            const userStatus = {
              empName: el.assignedTo,
              total: el.globalCount,
              ...amountCollected,
              ...allStatuses,
            };

            if (!userStatus.empName || userStatus.empName.trim() === "") {
              return null;
            }

            return userStatus;
          })
          .filter((userStatus) => userStatus !== null);

        this.analytics = data;
      } catch (error) {
        console.error(error);
      }
    },
    onDateSelected() {
      if (this.dates.length === 2) {
        this.startDate = this.formatDate(this.dates[0]);
        this.endDate = this.formatDate(this.dates[1]);
        this.fetchCaseAnalytics();
      } else {
        this.startDate = this.formatDate(this.dates[0]);
        this.endDate = null;
      }
    },
    formatDate(dateString) {
      return this.$dayjs(dateString).format("YYYY-MM-DD");
    },
    visibleDate(date) {
      return this.$dayjs(date).format("DD-MM-YYYY");
    },
    exportToXLSX() {
      const dataToExport = this.analytics.map((item) => {
        const exportItem = {};
        this.headers.forEach((header) => {
          exportItem[header.text] = item[header.value];
        });
        return exportItem;
      });
      const XLSX = require("xlsx");
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Case Analytics");

      XLSX.utils.sheet_add_aoa(
        ws,
        [this.headers.map((header) => header.text)],
        { origin: "A1" }
      );

      const currentDate = this.$dayjs().format("DD/MM/YYYY");
      XLSX.writeFile(
        wb,
        `${this.caseConfig.name}_Case_Analytics_${currentDate}.xlsx`
      );
    },
    handleInput() {
      this.fetchCaseAnalytics();
    },
    onDateCleared() {
      this.dates = [];
      this.startDate = null;
      this.endDate = null;
      this.fetchCaseAnalytics();
    },
  },
  watch: {
    caseConfig: {
      handler(config) {
        if (config) {
          const fm = mergeOptions(config.userDefinedCaseStatus);
          const mergedOptionsDetails = mergeOptionsDetails(
            config.userDefinedCaseStatus
          );
          this.fetchCaseAnalytics();
          this.headers = [
            { text: "Employee Name", value: "empName", sortable: false },
          ];

          fm.forEach((status) => {
            if (!this.headers.some((header) => header.value === status)) {
              this.headers.push({
                text: status,
                value: status,
                sortable: false,
              });
            }
          });
          this.headers.push({
            text: this.unAttended,
            value: this.unAttended,
            sortable: false,
          });
          this.headers.push({
            text: "Grand Total",
            value: "total",
            sortable: false,
          });

          const Columns = mergedOptionsDetails
            .filter((option) => option.amountVisibility)
            .map((option) => ({
              text: `${option.code} Amt.`,
              value: `${option.code}_amount`,
              key: `${option.code}`,
              width: "100px",
              sortable: false,
            }));
          this.headers.push(...Columns);
        }
      },
      immediate: true,
    },
  },
  mounted() {},
};
</script>

<style>
.wfm-search-fields.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 40px !important;
}
</style>
